<template>
  <div class="wholesaler-order-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="back-btn" @click="goBack">{{ $t('wholesaler.WholesalerOrderDetail.line5_text') }}</div>
      <h1>{{ $t('wholesaler.WholesalerOrderDetail.line6_text') }}</h1>
      <div class="order-no">
        {{ order.orderNo }}
        <button v-if="order.id" class="btn-share-small" @click="showShareDialog = true">{{ $t('wholesaler.WholesalerOrderDetail.line9_text') }}</button>
      </div>
    </div>

    <!-- 分享弹窗 -->
    <WholesalerShareDialog
      v-if="showShareDialog && order.id"
      type="order"
      :item-id="order.id"
      @close="showShareDialog = false"
      @created="onShareCreated"
    />

    <div v-loading="loading" class="detail-content">
      <!-- 状态卡片 -->
      <div class="status-bar" :class="statusBarClass">
        <div class="s-icon">{{ statusIcon }}</div>
        <div class="s-info">
          <div class="s-title">{{ statusTitle }}</div>
          <div class="s-sub">{{ order.createdAt }} · {{ order.sourceChannel }}</div>
        </div>
      </div>

      <!-- 时间线 -->
      <div class="timeline-wrap">
        <h3>{{ $t('wholesaler.WholesalerOrderDetail.line34_text') }}</h3>
        <div class="timeline">
          <div class="track" :style="{ width: trackWidth }"></div>
          <div class="tl-step" v-for="(s, i) in timeline" :key="i">
            <div class="tl-dot" :class="dotClass(i)">{{ s.icon }}</div>
            <div class="tl-label" :class="{ active: i === currentStep }">{{ s.label }}</div>
            <div class="tl-time">{{ s.time || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 模块1: 商品 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">📦</span><span class="title">{{ $t('wholesaler.WholesalerOrderDetail.line48_text') }}</span>
          <span class="badge blue">{{ order.items ? order.items.length : 0 }} {{ $t('order.items') }}</span>
        </div>
        <div class="module-body">
          <div class="prod-item" v-for="item in order.items" :key="item.id">
            <div class="prod-info">
              <div class="prod-name">{{ item.productName }}</div>
              <div class="prod-spec">{{ item.specification || '' }}</div>
              <span class="prod-tax" :class="item.taxRate && item.taxRate > 0 ? 'gst' : 'free'">
                {{ item.taxRate && item.taxRate > 0 ? 'GST ' + Math.round(item.taxRate * 100) + '%' : $t('order.taxFree') }}
              </span>
            </div>
            <div class="prod-right">
              <div class="prod-amount">S$ {{ formatAmount(item.subtotal) }}</div>
              <div class="prod-qty">×{{ item.quantity }}</div>
            </div>
          </div>
          <div style="margin-top: 12px;">
            <div class="amt-row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line66_text') }}</span><span class="value">S$ {{ formatAmount(order.subtotal) }}</span></div>
            <div class="amt-row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line67_text') }}</span><span class="value">S$ {{ formatAmount(order.gstAmount) }}</span></div>
            <div class="amt-row total"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line68_text') }}</span><span class="value">S$ {{ formatAmount(order.totalAmount) }}</span></div>
            <!-- 已付金额（按状态分组累加） -->
            <div v-if="Object.keys(paidByStatus).length > 0" class="amt-row" style="border-top:2px solid #f1f5f9;margin-top:8px;padding-top:12px">
              <span class="label" style="font-size:15px;font-weight:700;color:#1e293b">{{ $t('wholesaler.WholesalerOrderDetail.line71_text') }}</span>
              <span class="value" style="display:flex;gap:16px;flex-wrap:wrap">
                <span v-for="(g, k) in paidByStatus" :key="k" :style="{ color: g.color, fontSize:'16px', fontWeight:'700' }">{{ g.label }} S$ {{ g.amount }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 模块2: 档口客户 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">🏪</span><span class="title">{{ $t('wholesaler.WholesalerOrderDetail.line83_text') }}</span>
        </div>
        <div class="module-body">
          <div style="display:flex;align-items:center;gap:12px;padding:4px 0;">
            <div class="ws-avatar">👤</div>
            <div style="flex:1">
              <div class="ws-name">{{ order.customerName || $t('wholesaler.WholesalerOrderDetail.customer') }}</div>
              <div class="ws-desc">{{ order.customerPhone || $t('wholesaler.WholesalerOrderDetail.noPhone') }}</div>
            </div>
            <el-button size="mini" @click="callCustomer">{{ $t('wholesaler.WholesalerOrderDetail.line92_text') }}</el-button>
            <el-button size="mini" type="success" @click="contactCustomerWhatsapp" :disabled="!order.customerWhatsapp">💬 WhatsApp</el-button>
          </div>
          <div style="margin-top:8px">
            <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line95_text') }}</span><span class="value">{{ orderTypeText }}</span></div>
            <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line96_text') }}</span><span class="value mono">{{ order.invoiceNo || '-' }}</span></div>
            <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line97_text') }}</span><span class="value mono small">{{ order.invoiceUuid || '-' }}</span></div>
          </div>
        </div>
      </div>

      <!-- 配送任务信息 -->
      <div v-if="deliveryTask && deliveryTask.status !== 'CANCELLED'" class="module">
        <div class="module-header">
          <span class="icon">📦</span><span class="title">{{ $t('wholesaler.WholesalerOrderDetail.line105_text') }}</span>
        </div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line108_text') }}</span><span class="value">{{ deliveryTask.taskNo }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line109_text') }}</span><span class="value">{{ deliveryTask.taskType === 'ASSIGNED' ? $t('wholesaler.WholesalerOrderDetail.assigned') : $t('wholesaler.WholesalerOrderDetail.contractor') }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line110_text') }}</span><span class="value">{{ taskStatusText }}</span></div>
          <div class="row" v-if="deliveryTask.driverName"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line111_text') }}</span><span class="value">{{ deliveryTask.driverName }} (ID: {{ deliveryTask.driverId }})</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line112_text') }}</span><span class="value">{{ deliveryTask.pickupAddress || '-' }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line113_text') }}</span><span class="value">{{ deliveryTask.pickupContact || '-' }} {{ deliveryTask.pickupPhone || '' }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line114_text') }}</span><span class="value">{{ deliveryTask.deliveryAddress || '-' }}</span></div>
          <div class="row"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line115_text') }}</span><span class="value">{{ deliveryTask.deliveryContact || '-' }} {{ deliveryTask.deliveryPhone || '' }}</span></div>
          <div class="row" v-if="deliveryTask.assignedAt"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line116_text') }}</span><span class="value">{{ deliveryTask.assignedAt }}</span></div>
          <div class="row" v-if="deliveryTask.remark"><span class="label">{{ $t('wholesaler.WholesalerOrderDetail.line117_text') }}</span><span class="value">{{ deliveryTask.remark }}</span></div>
        </div>
      </div>

      <!-- 安排配送按钮（未安排配送任务或任务已取消时显示） -->
      <div v-if="canCreateTask" class="module">
        <div class="module-body" style="text-align:center; padding: 20px;">
          <button class="btn-arrange-big" @click="openTaskDialog">{{ $t('wholesaler.WholesalerOrderDetail.line124_text') }}</button>
        </div>
      </div>

      <!-- 模块5: 支付与售后 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">💳</span><span class="title">{{ $t('wholesaler.WholesalerOrderDetail.line131_text') }}</span>
        </div>
        <div class="module-body">
          <!-- 支付流水列表（只读显示，退款走售后） -->
          <div v-if="paymentRecords && paymentRecords.length > 0" style="margin-bottom:16px">
            <div style="font-size:13px;font-weight:600;color:#64748b;margin-bottom:8px">{{ $t('wholesaler.WholesalerOrderDetail.line136_text') }}</div>
            <div class="record-list">
              <div class="record-item" v-for="r in paymentRecords" :key="r.id" :class="{ 'is-pending': r.status === 'pending_confirm' }">
                <div class="record-header">
                  <span class="record-no">{{ r.paymentNo }}</span>
                  <span class="record-status" :class="getRecordStatusClass(r.status)">{{ getRecordStatusText(r.status) }}</span>
                </div>
                <div class="record-body">
                  <span class="record-amount">S$ {{ formatAmount(r.amount) }}</span>
                  <span class="record-method">{{ r.paymentMethod }}</span>
                </div>
                <div class="record-footer">
                  <span class="record-time">{{ r.createdAt || '-' }}</span>
                  <span v-if="r.confirmedAt" class="record-confirmed">{{ $t('wholesaler.WholesalerOrderDetail.line157_text') }}: {{ r.confirmedAt }}</span>
                </div>
                <!-- 付款凭证 -->
                <div v-if="r.transactionRef" style="margin-top:8px">
                  <img :src="getImageUrl(r.transactionRef)" :alt="$t('wholesaler.WholesalerOrderDetail.line153_alt')" class="proof-image" @click="previewProof(r.transactionRef)" />
                </div>
                <!-- 审核按钮 -->
                <div v-if="r.status === 'pending_confirm'" class="record-actions">
                  <el-button size="mini" type="success" @click="approveRecord(r)">{{ $t('wholesaler.WholesalerOrderDetail.line157_text') }}</el-button>
                  <el-button size="mini" type="danger" @click="rejectRecord(r)">{{ $t('wholesaler.WholesalerOrderDetail.line158_text') }}</el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- 售后记录 -->
          <div style="font-size:13px;font-weight:600;color:#64748b;margin-bottom:8px">{{ $t('wholesaler.WholesalerOrderDetail.line165_text') }}</div>
          <!-- 有售后记录时显示 -->
          <div v-if="afterSales.length > 0">
            <div
              v-for="as in afterSales"
              :key="as.id"
              class="as-card"
              @click="$router.push(`/wholesaler/after-sales/${as.id}`)"
            >
              <div class="as-header">
                <span class="as-no">{{ as.afterSalesNo }}</span>
                <span class="as-status" :class="asStatusClass(as.status)">{{ asStatusText(as.status) }}</span>
              </div>
              <div class="as-body">
                <div class="as-left">
                  <div class="as-reason">{{ asReasonIcon(as.reasonCode) }} {{ asReasonText(as.reasonCode) }}</div>
                  <div class="as-time">{{ as.requestedAt || as.createdAt }} · {{ asResolutionText(as.resolutionType) }}</div>
                </div>
                <div class="as-right">
                  <div class="as-amount">S$ {{ centsToAmount(as.refundAmount) }}</div>
                  <div class="as-sub">{{ asResolutionStatusText(as.resolutionStatus) }}</div>
                </div>
              </div>
            </div>
            <!-- 打印退单小票 -->
            <div style="margin-top:8px;">
              <el-button size="small" style="width:100%" @click="printRefundReceipt">{{ $t('wholesaler.WholesalerOrderDetail.line191_text') }}</el-button>
            </div>
          </div>

          <!-- 无售后记录时显示 -->
          <div v-else>
            <div class="empty-state">
              <div class="empty-icon">✅</div>
              <div class="empty-text">{{ $t('wholesaler.WholesalerOrderDetail.line199_text') }}</div>
            </div>
          </div>

          <!-- 代客下单申请售后按钮 -->
          <div v-if="canShowAfterSales" style="margin-top:8px">
            <el-button type="primary" style="width:100%" @click="handleApplyAfterSales">
              {{ $t('wholesaler.WholesalerOrderDetail.applyAfterSales') }}
            </el-button>
          </div>
        </div>
      </div>

      <div style="height:80px"></div>
    </div>

    <!-- 底部整单操作栏 -->
    <div class="action-bar">
      <el-button v-if="canCancelOrder" type="danger" size="small" @click="handleCancel">{{ $t('wholesaler.WholesalerOrderDetail.line217_text') }}</el-button>
      <el-button v-if="canShowAfterSales" type="warning" size="small" @click="handleApplyAfterSales">{{ $t('wholesaler.WholesalerOrderDetail.line218_text') }}</el-button>
      <el-button v-if="order.paymentStatus !== 'paid'" type="success" size="small" @click="openPaymentDialog">{{ $t('wholesaler.WholesalerOrderDetail.line219_text') }}</el-button>
      <el-button type="success" size="small" @click="handleCloudPrint" :loading="cloudPrinting">{{ $t('wholesaler.WholesalerOrderDetail.line220_text') }}</el-button>
      <el-button type="primary" size="small" @click="handleBluetoothPrint" :loading="bluetoothPrinting">{{ $t('wholesaler.WholesalerOrderDetail.line221_text') }}</el-button>
    </div>

    <!-- 收款弹窗 -->
    <el-dialog
      :title="$t('wholesaler.WholesalerOrderDetail.line226_title')"
      :visible.sync="showPaymentDialog"
      width="90%"
      class="payment-dialog"
    >
      <div class="payment-form">
        <div class="payment-info">📋 {{ $t('wholesaler.WholesalerOrderDetail.order') }}: {{ order.orderNo }} · {{ $t('wholesaler.WholesalerOrderDetail.line68_text') }}: S$ {{ formatAmount(order.totalAmount) }}</div>

        <!-- 支付方式 -->
        <div class="form-section">
          <div class="form-label">{{ $t('wholesaler.WholesalerOrderDetail.line236_text') }}</div>
          <div class="payment-method-tags">
            <span
              v-for="m in paymentMethods"
              :key="m.value"
              class="payment-tag"
              :class="{ active: paymentForm.paymentMethod === m.value }"
              @click="paymentForm.paymentMethod = m.value"
            >
              {{ m.icon }} {{ m.label }}
            </span>
          </div>
        </div>

        <!-- 收款金额 -->
        <div class="form-section">
          <div class="form-label">{{ $t('wholesaler.WholesalerOrderDetail.line252_text') }}</div>
          <el-input
            v-model="paymentForm.amount"
            type="number"
            :placeholder="$t('wholesaler.WholesalerOrderDetail.line256_placeholder')"
            :step="0.01"
            :min="0.01"
          >
            <template slot="prepend">S$</template>
          </el-input>
        </div>

        <!-- 上传凭证 -->
        <div class="form-section">
          <div class="form-label">📸 {{ $t('wholesaler.WholesalerOrderDetail.paymentProofOptional') }}</div>
          <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            list-type="picture-card"
            :file-list="paymentForm.proofFiles"
            :on-success="handlePaymentUploadSuccess"
            :on-remove="handlePaymentUploadRemove"
            :limit="3"
            accept="image/*"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </div>

        <!-- 备注 -->
        <div class="form-section">
          <div class="form-label">{{ $t('wholesaler.WholesalerOrderDetail.remarkOptional') }}</div>
          <el-input
            v-model="paymentForm.remark"
            type="textarea"
            :rows="2"
            :placeholder="$t('wholesaler.WholesalerOrderDetail.enterRemark')"
            maxlength="200"
            show-word-limit
          ></el-input>
        </div>

        <div class="form-actions">
          <el-button @click="showPaymentDialog = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :disabled="!paymentForm.paymentMethod || !paymentForm.amount" @click="submitPayment">{{ $t('wholesaler.WholesalerOrderDetail.confirmCollect') }}</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 代客下单申请售后弹窗 -->
    <el-dialog
      :title="$t('wholesaler.WholesalerOrderDetail.applyAfterSales')"
      :visible.sync="showApplyDialog"
      width="90%"
      class="apply-dialog"
    >
      <div v-if="order && order.id" class="apply-form">
        <div class="apply-info">📋 {{ $t('wholesaler.WholesalerOrderDetail.order') }}: {{ order.orderNo }} · {{ $t('wholesaler.WholesalerOrderDetail.customer') }}: {{ order.customerName || $t('wholesaler.WholesalerOrderDetail.unknown') }} · S$ {{ formatAmount(order.totalAmount) }}</div>

        <!-- 问题原因 -->
        <div class="form-section">
          <div class="form-label">{{ $t('wholesaler.WholesalerOrderDetail.reason') }}</div>
          <div class="reason-tags">
            <span
              v-for="r in reasons"
              :key="r.value"
              class="reason-tag"
              :class="{ active: applyForm.reasonCode === r.value }"
              @click="applyForm.reasonCode = r.value"
            >
              {{ r.icon }} {{ r.label }}
            </span>
          </div>
        </div>

        <!-- 问题描述 -->
        <div class="form-section">
          <div class="form-label">{{ $t('wholesaler.WholesalerOrderDetail.description') }}</div>
          <el-input
            v-model="applyForm.reasonText"
            type="textarea"
            :rows="3"
            :placeholder="$t('wholesaler.WholesalerOrderDetail.describeProblem')"
            maxlength="500"
            show-word-limit
          ></el-input>
        </div>

        <!-- 上传凭证 -->
        <div class="form-section">
          <div class="form-label">📸 {{ $t('wholesaler.WholesalerOrderDetail.uploadProof') }}</div>
          <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            list-type="picture-card"
            :file-list="applyForm.evidenceFiles"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :limit="9"
            accept="image/*"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </div>

        <div class="form-actions">
          <el-button @click="showApplyDialog = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :disabled="!applyForm.reasonCode" @click="submitApply">{{ $t('wholesaler.WholesalerOrderDetail.submitApplication') }}</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 配送任务弹窗 -->
    <el-dialog
        :title="$t('wholesaler.WholesalerOrderDetail.createDeliveryTask')"
        :visible.sync="taskDialogVisible"
        width="420px"
        :before-close="() => { taskDialogVisible = false; }">
      <div class="task-type-selection">
        <div class="section-title">📋 {{ $t('wholesaler.WholesalerOrderDetail.taskType') }}</div>
        <el-radio-group v-model="taskType" class="task-radio-group">
          <el-radio-button label="ASSIGNED">👤 {{ $t('wholesaler.WholesalerOrderDetail.assignDriver') }}</el-radio-button>
          <el-radio-button label="CONTRACTOR">🎯 {{ $t('wholesaler.WholesalerOrderDetail.publishContractor') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 众包配置 -->
      <div class="contractor-config" v-if="taskType === 'CONTRACTOR'">
        <el-divider />
        <div class="section-title">⚙️ {{ $t('wholesaler.WholesalerOrderDetail.contractorConfig') }}</div>
        <el-form :model="contractorConfig" label-width="100px" size="small">
          <el-form-item :label="$t('wholesaler.WholesalerOrderDetail.deliveryFee')">
            <el-input-number v-model="contractorConfig.fee" :min="0" :max="1000" :precision="2" :step="0.5" style="width: 100%" />
          </el-form-item>
          <el-form-item :label="$t('wholesaler.WholesalerOrderDetail.estimatedDuration')">
            <el-input-number v-model="contractorConfig.estimatedDuration" :min="10" :max="480" :precision="0" :step="10" style="width: 100%" />
            <div class="form-tip">{{ $t('wholesaler.WholesalerOrderDetail.estimatedDurationTip') }}</div>
          </el-form-item>
        </el-form>
      </div>

      <el-divider />

      <!-- 仓库选择 -->
      <div class="warehouse-selection">
        <div class="section-title">📦 {{ $t('wholesaler.WholesalerOrderDetail.selectWarehouse') }}</div>
        <div class="warehouse-list">
          <div
            v-for="warehouse in availableWarehouses"
            :key="warehouse.id"
            :class="['warehouse-card', { selected: selectedWarehouseId === warehouse.id, 'insufficient': !isStockSufficient(warehouse) }]"
            @click="isStockSufficient(warehouse) && (selectedWarehouseId = warehouse.id)">
            <el-radio :value="selectedWarehouseId" :label="warehouse.id"></el-radio>
            <div class="warehouse-info">
              <div class="warehouse-name">{{ warehouse.warehouseName }}</div>
              <div class="product-stocks" v-if="warehouse.productStocks">
                <div v-if="warehouse.productStocks.length === 0" class="no-stock">⚠️ {{ $t('wholesaler.WholesalerOrderDetail.noStockRecord') }}</div>
                <div v-else v-for="stock in warehouse.productStocks" :key="stock.productId || stock.id" class="stock-item">
                  <span class="stock-name">{{ stock.productName || $t('wholesaler.WholesalerOrderDetail.product') }}</span>
                  <span class="stock-qty">{{ $t('wholesaler.WholesalerOrderDetail.stock') }}: {{ stock.stockQuantity || 0 }} {{ stock.unit || '' }}</span>
                  <el-tag
                    :type="(stock.stockQuantity || 0) >= (orderQuantityMap[stock.productId] || 0) ? 'success' : 'warning'"
                    size="mini">
                    {{ $t('wholesaler.WholesalerOrderDetail.orderQty') }}: {{ orderQuantityMap[stock.productId] || 0 }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 配送员选择 -->
      <div class="driver-selection" v-if="taskType === 'ASSIGNED'">
        <div class="section-title">🚚 {{ $t('wholesaler.WholesalerOrderDetail.selectDriver') }}</div>
        <div class="driver-list">
          <div
            v-for="driver in availableDrivers"
            :key="driver.id"
            :class="['driver-card', { selected: selectedDriverId === driver.id }]"
            @click="selectedDriverId = driver.id">
            <el-radio :value="selectedDriverId" :label="driver.id"></el-radio>
            <div class="driver-info">
              <div class="driver-name">{{ driver.name }}</div>
              <div class="driver-phone">📞 {{ driver.phone }}</div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="taskDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleCreateTask">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderDetail, cancelOrder } from '@/services/order';
import api from '@/services/api';
import WholesalerShareDialog from '@/components/WholesalerShareDialog.vue';
import { getImageUrl } from '@/utils/image';
import { getReceivableForPrint } from '@/api/report';
import { getWholesalerId } from '@/utils/wholesaler';

export default {
  name: 'WholesalerOrderDetail',
  components: {
    WholesalerShareDialog
  },
  data() {
    return {
      order: {},
      loading: false,
      printing: false,
      cloudPrinting: false,
      bluetoothPrinting: false,
      afterSales: [],
      paymentRecords: [],
      deliveryTask: null,
      showApplyDialog: false,
      showShareDialog: false,
      showPaymentDialog: false,
      allowNegativeStock: false, // 零负库存开关
      paymentForm: {
        paymentMethod: '',
        amount: '',
        proofFiles: [],
        remark: ''
      },
      paymentMethods: [
        { value: '现金', icon: '💵', label: this.$t('order.cash') + ' Cash' },
        { value: '转账', icon: '🏦', label: this.$t('order.transfer') },
        { value: '信用卡', icon: '💳', label: this.$t('payment.creditCard') },
        { value: 'PayNow', icon: '📱', label: 'PayNow' }
      ],
      // 配送任务弹窗相关
      taskDialogVisible: false,
      taskType: 'ASSIGNED',
      selectedWarehouseId: '',
      selectedDriverId: null,
      availableWarehouses: [],
      availableDrivers: [],
      currentOrder: null,
      orderQuantityMap: {},
      contractorConfig: {
        fee: 15.00,
        estimatedDuration: 60,
        remark: ''
      },
      resolveDialog: null,
      rejectDialog: null,
      applyForm: {
        reasonCode: '',
        reasonText: '',
        evidenceFiles: []
      },
      reasons: [
        { value: 'DAMAGED', icon: '💔', label: this.$t('afterSales.reasonDamaged') },
        { value: 'WRONG_ITEM', icon: '❌', label: this.$t('afterSales.reasonWrongItem') },
        { value: 'NOT_RECEIVED', icon: '📦', label: this.$t('afterSales.reasonNotReceived') },
        { value: 'WRONG_SPEC', icon: '📐', label: this.$t('afterSales.reasonWrongSpec') },
        { value: 'MISSING', icon: '🔍', label: this.$t('afterSales.reasonMissingItem') },
        { value: 'NOT_WANTED', icon: '🙅', label: this.$t('afterSales.reasonDontWant') }
      ]
    };
  },
  computed: {
    /** 按状态分组的已付金额累加 */
    pendingAmount() {
      const total = Number(this.order.totalAmount || 0);
      const confirmedSum = (this.paymentRecords || [])
        .filter(r => r.status === 'confirmed' || r.status === 'success')
        .reduce((sum, r) => sum + Number(r.amount || 0), 0);
      return Math.max(0, total - confirmedSum);
    },
    paidByStatus() {
      if (!this.paymentRecords || this.paymentRecords.length === 0) return {};
      const groups = {
        pending_confirm: { label: this.$t('wholesaler.WholesalerOrderDetail.paymentPendingConfirm'), amount: 0, color: '#f59e0b' },
        confirmed: { label: this.$t('wholesaler.WholesalerOrderDetail.paymentConfirmedLabel'), amount: 0, color: '#10b981' },
        success: { label: this.$t('wholesaler.WholesalerOrderDetail.paymentSuccessLabel'), amount: 0, color: '#10b981' },
        failed: { label: this.$t('wholesaler.WholesalerOrderDetail.paymentFailedLabel'), amount: 0, color: '#ef4444' },
      };
      for (const r of this.paymentRecords) {
        const g = groups[r.status];
        if (g) g.amount += Number(r.amount || 0);
      }
      const result = {};
      for (const [k, v] of Object.entries(groups)) {
        if (v.amount > 0) result[k] = { ...v, amount: v.amount.toFixed(2) };
      }
      return result;
    },
    timeline() {
      const statuses = [
        { label: this.$t('wholesaler.WholesalerOrderDetail.confirmedStatus'), field: 'createdAt' },
        { label: this.$t('wholesaler.WholesalerOrderDetail.awaitingPickup'), field: null },
        { label: this.$t('wholesaler.WholesalerOrderDetail.dispatchedStatus'), field: null },
        { label: this.$t('wholesaler.WholesalerOrderDetail.deliveredStatus'), field: null },
        { label: this.$t('wholesaler.WholesalerOrderDetail.cancelledStatus'), field: null }
      ];
      const s = (this.order.status || '').toUpperCase();
      let currentIdx = 0;
      if (s === 'CANCELLED') currentIdx = -1;
      else if (s === 'DELIVERED') currentIdx = 3;
      else if (s === 'DISPATCHED') currentIdx = 2;
      else if (s === 'AWAITING_PICKUP') currentIdx = 1;
      else if (s === 'CONFIRMED') currentIdx = 0;

      return statuses.map((step, i) => ({
        icon: currentIdx === -1 ? (i === 4 ? '❌' : '✓') : (i <= currentIdx ? '✓' : String(i + 1)),
        label: step.label,
        time: step.field ? this.order[step.field] : (i === currentIdx ? this.order.createdAt : null)
      }));
    },
    currentStep() {
      const s = (this.order.status || '').toUpperCase();
      if (s === 'CANCELLED') return 4;
      if (s === 'DELIVERED') return 3;
      if (s === 'DISPATCHED') return 2;
      if (s === 'AWAITING_PICKUP') return 1;
      return 0;
    },
    trackWidth() {
      return `${((this.currentStep + 1) / 5) * 100}%`;
    },
    statusIcon() {
      const m = { confirmed: '📋', awaiting_pickup: '📦', dispatched: '🚚', delivered: '🎉', cancelled: '❌' };
      return m[this.order.status] || '⏳';
    },
    statusTitle() {
      const m = {
        confirmed: this.$t('wholesaler.WholesalerOrderDetail.confirmedStatus'),
        awaiting_pickup: this.$t('wholesaler.WholesalerOrderDetail.awaitingPickup'),
        dispatched: this.$t('wholesaler.WholesalerOrderDetail.dispatchedStatus'),
        delivered: this.$t('wholesaler.WholesalerOrderDetail.deliveredStatus'),
        cancelled: this.$t('wholesaler.WholesalerOrderDetail.cancelledStatus')
      };
      return m[this.order.status] || this.$t('wholesaler.WholesalerOrderDetail.unknownStatus');
    },
    statusBarClass() {
      if (this.order.status === 'cancelled') return 'cancelled';
      if (['delivered', 'dispatched', 'awaiting_pickup', 'confirmed'].includes(this.order.status)) return 'processing';
      return 'pending';
    },
    orderTypeText() {
      const m = { 
        SELF: this.$t('order.orderSelf'), 
        AGENT: this.$t('order.orderAgent'), 
        DELIVERY: this.$t('order.orderDelivery') 
      };
      return m[this.order.orderType] || this.order.orderType;
    },
    deliveryMethodText() {
      return this.order.deliveryMethod === 'SELF_PICKUP' ? this.$t('order.selfPickup') : this.order.deliveryMethod || '-';
    },
    canCreateTask() {
      const taskCancelled = this.deliveryTask && this.deliveryTask.status === 'CANCELLED';
      return (!this.deliveryTask || taskCancelled) && this.order.deliveryMethod !== 'SELF_PICKUP' && 
             !['completed', 'cancelled', 'CANCELLED'].includes(this.order.status);
    },
    taskStatusText() {
      if (!this.deliveryTask) return '';
      const m = {
        'PENDING': this.$t('wholesaler.WholesalerOrderDetail.awaitingPickup'),
        'WAITING_FOR_DRIVER': this.$t('wholesaler.WholesalerOrderDetail.waitingForGrab'),
        'DELIVERING': this.$t('wholesaler.WholesalerOrderDetail.dispatchedStatus'),
        'COMPLETED': this.$t('wholesaler.WholesalerOrderDetail.deliveredStatus'),
        'CANCELLED': this.$t('wholesaler.WholesalerOrderDetail.cancelledStatus')
      };
      return m[this.deliveryTask.status] || this.deliveryTask.status;
    },
    hasPendingAfterSales() {
      return this.afterSales.some(as =>
        ['PENDING_REVIEW', 'REVIEWED'].includes(as.status)
      );
    },
    canCancelOrder() {
      return this.order.canCancel === true;
    },
    canShowAfterSales() {
      return this.order.canAfterSales === true;
    },
    canApplyAfterSales() {
      // 订单已送达/已完成且无进行中的售后可申请
      const completedStatuses = ['delivered', 'completed'];
      const statusOk = completedStatuses.includes((this.order.status || '').toLowerCase());
      const hasActiveAfterSales = this.afterSales.some(
        as => !['COMPLETED', 'REJECTED', 'CANCELLED'].includes(as.status)
      );
      return statusOk && !hasActiveAfterSales;
    },
    uploadUrl() {
      return `${process.env.VUE_APP_API_URL}/files/upload-image`;
    },
    uploadHeaders() {
      const token = localStorage.getItem('auth_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  },
  created() {
    console.log('created');
    this.loadOrderDetail();
  },
  activated() {
    console.log('activated');
    this.loadOrderDetail();
  },
  watch: {
    order: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadAfterSales();
        }
      },
      immediate: false
    }
  },
  methods: {
    getImageUrl,
    onShareCreated(shareData) {
      this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.shareLinkGenerated'));
    },
    openPaymentDialog() {
      // 默认金额 = 待收款额度
      this.paymentForm.amount = this.pendingAmount.toFixed(2);
      this.paymentForm.paymentMethod = '';
      this.paymentForm.proofFiles = [];
      this.paymentForm.remark = '';
      this.showPaymentDialog = true;
    },
    handlePaymentUploadSuccess(response, file) {
      if (response && response.url) {
        // 存储相对路径，但为 el-upload 显示提供完整URL
        const fullUrl = getImageUrl(response.url);
        this.paymentForm.proofFiles.push({ 
          name: file.name, 
          url: fullUrl,
          rawUrl: response.url // 保存原始相对路径用于提交
        });
      }
    },
    handlePaymentUploadRemove(file) {
      this.paymentForm.proofFiles = this.paymentForm.proofFiles.filter(f => f.url !== file.url);
    },
    async submitPayment() {
      if (!this.paymentForm.paymentMethod) {
        this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.selectPaymentMethodWarn'));
        return;
      }
      if (!this.paymentForm.amount || Number(this.paymentForm.amount) <= 0) {
        this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.enterValidAmountWarn'));
        return;
      }

      try {
        // 构建凭证 URL（多张图片用逗号分隔，优先使用原始相对路径）
        const proofUrls = this.paymentForm.proofFiles.map(f => f.rawUrl || f.url).join(',');
        
        // 创建支付记录
        await api.post(`/orders/${this.order.id}/initiate-payment`, {
          paymentMethod: this.paymentForm.paymentMethod,
          amount: Number(this.paymentForm.amount),
          transactionRef: proofUrls || null,
          remark: this.paymentForm.remark || null
        });

        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.paymentRecordCreated'));
        this.showPaymentDialog = false;
        this.paymentForm = { paymentMethod: '', amount: '', proofFiles: [], remark: '' };
        await this.loadOrderDetail();
        await this.loadPaymentRecords();
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.collectFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    },
    async loadOrderDetail() {
      console.log('✅ loadOrderDetail start');
      this.loading = true;
      try {
        const id = this.$route.params.id;
        this.order = await getOrderDetail(id);
        console.log('🔍 this.order:',this.order);
        if (this.order?.id) {
          await this.loadPaymentRecords();
          await this.loadAfterSales();
          await this.loadDeliveryTask();
        }
      } catch (e) {
        console.error('❌ loadOrderDetail error:', e);
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.loadOrderFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally {
        console.log('✅ loadOrderDetail finally');
        this.loading = false;
      }
    },
    async loadDeliveryTask() {
      try {
        const res = await api.get(`/delivery/tasks/order/${this.order.id}`);
        this.deliveryTask = res || null;
      } catch (e) {
        this.deliveryTask = null;
      }
    },
    async openTaskDialog() {
      this.currentOrder = this.order;
      this.taskType = 'ASSIGNED';
      this.selectedDriverId = null;

      try {
        const wholesalerId = this.order.wholesalerId;
        console.log('🔍 wholesalerId:', wholesalerId);

        // 获取批发商信息
        const wholesalerRes = await api.get('/wholesalers/' + wholesalerId);
        console.log('🔍 wholesalerRes:', wholesalerRes);
        const wholesalerInfo = Array.isArray(wholesalerRes) ? wholesalerRes[0] : (wholesalerRes.data || wholesalerRes);
        console.log('🏪 wholesalerInfo:', JSON.stringify(wholesalerInfo, null, 2));
        console.log('📍 latitude:', wholesalerInfo?.latitude, 'longitude:', wholesalerInfo?.longitude);
        
        // ⭐ 保存零负库存开关状态
        this.allowNegativeStock = Boolean(wholesalerInfo?.allowNegativeStock);
        console.log('📦 零负库存开关:', this.allowNegativeStock);

        // 构建订单商品数量映射
        const orderItems = this.order.items || [];
        this.orderQuantityMap = {};
        orderItems.forEach(item => {
          if (item.productId) {
            this.orderQuantityMap[item.productId] = item.quantity || 0;
          }
        });

        // 构建仓库列表
        this.availableWarehouses = [];
        if (wholesalerInfo && wholesalerInfo.address) {
          this.availableWarehouses.push({
            id: 'default_shop',
            warehouseName: wholesalerInfo.companyName || wholesalerInfo.shopName || this.$t('wholesaler.WholesalerOrderDetail.shopWarehouse'),
            warehouseAddress: wholesalerInfo.address,
            latitude: wholesalerInfo.latitude || null,
            longitude: wholesalerInfo.longitude || null,
            isDefault: true,
            productStocks: []
          });
        }

        // 获取其他仓库
        const warehousesRes = await api.get('/warehouse/list', { params: { wholesalerId } });
        let otherWarehouses = Array.isArray(warehousesRes) ? warehousesRes : (warehousesRes.data || []);
        otherWarehouses = otherWarehouses.map(w => ({ ...w, productStocks: [] }));
        this.availableWarehouses = [...this.availableWarehouses, ...otherWarehouses];

        // 加载库存数据
        try {
          const productIds = orderItems.map(item => item.productId).filter(id => id != null);
          const stockRes = await api.get('/warehouse/stocks', {
            params: { wholesalerId, productIds },
            paramsSerializer: params => {
              const parts = [`wholesalerId=${params.wholesalerId}`];
              if (Array.isArray(params.productIds)) {
                params.productIds.forEach(id => parts.push(`productIds=${id}`));
              }
              return parts.join('&');
            }
          });
          let allStocks = Array.isArray(stockRes) ? stockRes : (stockRes.data?.data || stockRes.data?.items || stockRes.data || []);
          for (const warehouse of this.availableWarehouses) {
            if (warehouse.id === 'default_shop') {
              // ⭐ 店铺库存直接用商品的 shopStockQuantity，不查 product_warehouse_stock
              warehouse.productStocks = orderItems.map(item => ({
                productId: item.productId,
                productName: item.productName,
                stockQuantity: item.shopStockQuantity || 0,
                unit: item.unit || ''
              }));
            } else {
              warehouse.productStocks = allStocks
                .filter(s => s.warehouseId === warehouse.id)
                .map(s => ({ ...s, productName: orderItems.find(item => item.productId === s.productId)?.productName || '商品' }));
            }
          }
        } catch (e) {
          console.error('加载库存失败:', e);
          for (const warehouse of this.availableWarehouses) {
            warehouse.productStocks = orderItems.map(item => ({
              productId: item.productId,
              productName: item.productName,
              stockQuantity: 0
            }));
          }
        }

        // 默认选择库存充足的仓库
        const sufficientWarehouse = this.availableWarehouses.find(w => this.isStockSufficient(w));
        this.selectedWarehouseId = sufficientWarehouse?.id || this.availableWarehouses[0]?.id || '';
        console.log('📦 availableWarehouses:', this.availableWarehouses.map(w => ({ id: w.id, name: w.warehouseName, lat: w.latitude, lng: w.longitude })));
        console.log('📦 selectedWarehouseId:', this.selectedWarehouseId);

        // 获取配送员列表
        const driversRes = await api.get('/delivery/drivers/all', { params: { wholesalerId } });
        this.availableDrivers = Array.isArray(driversRes) ? driversRes : (driversRes.content || driversRes.data?.content || driversRes.data || []);

        if (this.availableDrivers.length === 0) {
          this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.noAvailableDriver'));
          return;
        }

        this.taskDialogVisible = true;
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.loadFailedMsg') + ': ' + (e.message || e));
      }
    },
    isStockSufficient(warehouse) {
      // ⭐ 零负库存开关开启时，跳过库存检查
      if (this.allowNegativeStock) return true;
      if (!warehouse || !warehouse.productStocks) return true;
      for (const stock of warehouse.productStocks) {
        const required = this.orderQuantityMap[stock.productId] || 0;
        const available = stock.stockQuantity || 0;
        if (available < required) return false;
      }
      return true;
    },
    async handleCreateTask() {
      const warehouse = this.availableWarehouses.find(w => w.id === this.selectedWarehouseId);
      if (!warehouse) {
        this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.selectWarehouseWarn'));
        return;
      }
      const wholesalerId = this.order.wholesalerId;

      try {
        if (this.taskType === 'CONTRACTOR') {
          // 发布众包任务
          await api.post('/delivery/tasks/contractor', {
            wholesalerId,
            orderId: this.currentOrder.id,
            warehouseId: this.selectedWarehouseId !== 'default_shop' ? this.selectedWarehouseId : 0,
            pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.WholesalerOrderDetail.warehouse'),
            deliveryAddress: this.currentOrder.deliveryAddress || this.$t('wholesaler.WholesalerOrderDetail.customerAddress'),
            pickupContact: warehouse.warehouseContact || warehouse.contactName || '',
            pickupPhone: warehouse.warehousePhone || warehouse.phone || '',
            deliveryContact: this.currentOrder.customerName || '',
            deliveryPhone: this.currentOrder.customerPhone || '',
            fee: this.contractorConfig.fee,
            estimatedDuration: this.contractorConfig.estimatedDuration,
            remark: ''
          });
          this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.contractorPublished'));
        } else {
          // 指派给配送员
          if (!this.selectedDriverId) {
            this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.selectDriverWarn'));
            return;
          }
          const selectedDriver = this.availableDrivers.find(d => d.id === this.selectedDriverId);
          const payload = {
            wholesalerId,
            driverId: this.selectedDriverId,
            orderId: this.currentOrder.id,
            customerId: this.currentOrder.customerId || null,
            warehouseId: this.selectedWarehouseId !== 'default_shop' ? this.selectedWarehouseId : 0,
            pickupAddress: warehouse.warehouseAddress || this.$t('wholesaler.WholesalerOrderDetail.warehouse'),
            pickupContact: warehouse.warehouseContact || warehouse.contactName || '',
            pickupPhone: warehouse.warehousePhone || warehouse.phone || '',
            driverName: selectedDriver ? selectedDriver.name : '',
            remark: ''
          };
          await api.post('/delivery/tasks/assign', payload);
          this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.taskCreated'));
        }

        this.taskDialogVisible = false;
        await this.loadDeliveryTask();
        await this.loadOrderDetail();
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.createFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    },
    async loadPaymentRecords() {
      try {
        const res = await api.get(`/orders/${this.order.id}/payment-records`);
        // API 拦截器已返回 response.data，res 就是数组
        this.paymentRecords = Array.isArray(res) ? res : (res.data || []);
        console.log('✅ 支付记录:', this.paymentRecords.length, '条', this.paymentRecords);
      } catch (e) {
        console.error('加载支付记录失败', e);
        this.paymentRecords = [];
      }
    },
    async approveRecord(record) {
      try {
        await this.$confirm(`确认审核通过流水 ${record.paymentNo}？`, this.$t('wholesaler.WholesalerOrderDetail.confirmedStatus'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        });
        await api.post(`/orders/payment-records/${record.id}/approve`);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.approvedStatus'));
        await this.loadPaymentRecords();
        await this.loadOrderDetail();
      } catch (e) {
        if (!this.isElCancel(e)) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.operationFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    async rejectRecord(record) {
      try {
        const { value } = await this.$prompt(this.$t('wholesaler.WholesalerOrderDetail.enterRejectReason'), this.$t('wholesaler.WholesalerOrderDetail.rejectedStatus'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          inputPattern: /.+/,
          inputErrorMessage: this.$t('wholesaler.WholesalerOrderDetail.enterRejectReason')
        });
        await api.post(`/orders/payment-records/${record.id}/reject`, { remark: value });
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.rejectedStatus'));
        await this.loadPaymentRecords();
      } catch (e) {
        if (!this.isElCancel(e)) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.operationFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    getRecordStatusClass(status) {
      const m = { pending_confirm: 'pending', success: 'success', failed: 'failed' };
      return m[status] || 'pending';
    },
    getRecordStatusText(status) {
      const m = { 
        pending_confirm: this.$t('wholesaler.WholesalerOrderDetail.pending'), 
        success: this.$t('wholesaler.WholesalerOrderDetail.paymentSuccessLabel'), 
        failed: this.$t('wholesaler.WholesalerOrderDetail.rejectedStatus') 
      };
      return m[status] || status;
    },
    previewProof(url) {
      window.open(url, '_blank');
    },
    async confirmPayment() {
      try {
        await this.$confirm(this.$t('wholesaler.WholesalerOrderDetail.paymentConfirmedMsg'), this.$t('wholesaler.WholesalerOrderDetail.confirmCollect'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        });
        await api.post(`/orders/${this.order.id}/confirm-payment`);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.paymentConfirmedMsg'));
        await this.loadOrderDetail();
      } catch (e) {
        if (!this.isElCancel(e)) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.operationFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    async handlePrint() {
      if (!this.printerConfig.machineCode) {
        this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.configurePrinterFirst'));
        this.$router.push('/wholesaler/printer-config');
        return;
      }
      this.printing = true;
      try {
        await api.post(`/orders/${this.order.id}/print`);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.printTaskSent'));
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.printFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally {
        this.printing = false;
      }
    },
    /**
     * 云打印订单小票
     */
    async handleCloudPrint() {
      this.cloudPrinting = true;
      try {
        await api.post(`/orders/${this.order.id}/print`);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.cloudPrintSent'));
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.cloudPrintFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally {
        this.cloudPrinting = false;
      }
    },
    /**
     * 蓝牙打印订单小票
     */
    async handleBluetoothPrint() {
      this.bluetoothPrinting = true;
      try {
        // 获取完整的订单数据（包含 items）
        const response = await api.get(`/wholesaler/orders/${this.order.id}`);
        if (!response.data || !response.data.success) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.loadOrderDataFailed'));
          return;
        }
        
        const fullOrder = response.data.data;
        
        // 获取应收款数据（用 fullOrder 而非 this.order，确保 customerId 存在）
        let receivableData = null;
        const customerId = fullOrder.customerId || this.order.customerId || this.order.customer?.id;
        const wholesalerId = getWholesalerId();
        if (customerId) {
          try {
            const res = await getReceivableForPrint(customerId, wholesalerId);
            if (res && res.success && res.data) {
              receivableData = res.data;
            }
          } catch (e) {
            console.warn('获取未收款汇总失败:', e.message);
          }
        }
        
        // 获取蓝牙打印机实例
        const { getPrinter } = await import('@/services/bluetooth-printer');
        const printer = getPrinter();
        
        // 连接打印机
        const connectResult = await printer.connect();
        if (!connectResult.success) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.bluetoothConnectFailed') + ': ' + connectResult.error);
          return;
        }
        
        // 打印订单小票
        await printer.printSingaporeOrder(fullOrder, null, receivableData);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.bluetoothPrintSuccess'));
        
      } catch (error) {
        console.error('蓝牙打印失败:', error);
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.bluetoothPrintFailed') + ': ' + (error.response?.data?.message || error.message));
      } finally {
        this.bluetoothPrinting = false;
      }
    },
    async handlePackComplete() {
      try {
        await completePacking(this.order.id);
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.packingCompleted'));
        this.loadOrderDetail();
      } catch (e) {
        this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.operationFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    },
    async handleCancel() {
      try {
        await this.$confirm(this.$t('wholesaler.WholesalerOrderDetail.cancelConfirmMsg'), this.$t('wholesaler.WholesalerOrderDetail.cancelTitle'), {
          confirmButtonText: this.$t('wholesaler.WholesalerOrderDetail.confirmCancel'),
          cancelButtonText: this.$t('wholesaler.WholesalerOrderDetail.thinkAgain'),
          type: 'warning'
        });
        await cancelOrder(this.order.id, { reason: this.$t('wholesaler.WholesalerOrderDetail.wholesalerCancelled') });
        this.$message.success(this.$t('wholesaler.WholesalerOrderDetail.orderCancelled'));
        await this.loadOrderDetail();
      } catch (e) {
        if (!this.isElCancel(e)) {
          this.$message.error(this.$t('wholesaler.WholesalerOrderDetail.cancelFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    callCustomer() {
      const p = this.order.customerPhone;
      if (!p) return this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.customerNoPhone'));
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (!isMobile) {
        this.$alert(`${this.$t('wholesaler.WholesalerOrderDetail.usePhoneCall')}：${p}`, this.$t('wholesaler.WholesalerOrderDetail.contactPhone'));
        return;
      }
      window.location.href = `tel:${p}`;
    },
    contactCustomerWhatsapp() {
      if (!this.order.customerWhatsapp) {
        this.$message.warning(this.$t('wholesaler.WholesalerOrderDetail.noCustomerWhatsapp'));
        return;
      }
      const orderNo = this.order.orderNo || '';
      const message = `你好，关于订单 ${orderNo}，我想和你沟通一下...`;
      const url = `https://wa.me/${this.order.customerWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    },
    dotClass(i) {
      if (this.currentStep === -1) return i === 0 ? 'cancelled' : '';
      if (i < this.currentStep) return 'done';
      if (i === this.currentStep) return 'current';
      return '';
    },
    formatAmount(amount) {
      const n = Number(amount || 0);
      return new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    isElCancel(e) {
      return e === 'cancel' || e === 'close' || (e && e.message === 'cancel');
    },
    goBack() {
      this.$router.back();
    },
    handleApplyAfterSales() {
      const hasPending = this.afterSales.some(as =>
        ['PENDING_REVIEW', 'REVIEWED'].includes(as.status)
      );
      if (hasPending) {
        this.$message.warning('该订单已有未审批的售后申请');
        return;
      }
      this.showApplyDialog = true;
    },
    goToAfterSalesList() {
      this.$router.push(`/wholesaler/after-sales?orderId=${this.order.id}`);
    },
    async loadAfterSales() {
      try {
        const res = await api.get('/after-sales/list', {
          params: { orderId: this.order.id, page: 0, size: 50 }
        });
        this.afterSales = res.content || [];
      } catch (e) {
        console.error('加载售后记录失败', e);
        this.afterSales = [];
      }
    },
    handleUploadSuccess(response, file) {
      if (response && response.url) {
        this.applyForm.evidenceFiles.push({ name: file.name, url: response.url });
      }
    },
    handleUploadRemove(file) {
      this.applyForm.evidenceFiles = this.applyForm.evidenceFiles.filter(f => f.url !== file.url);
    },
    async submitApply() {
      if (!this.applyForm.reasonCode) {
        this.$message.warning('请选择问题原因');
        return;
      }
      try {
        const evidence = this.applyForm.evidenceFiles.map(f => f.url);
        await api.post('/after-sales/apply', {
          orderId: this.order.id,
          customerId: this.order.customerId,
          reasonCode: this.applyForm.reasonCode,
          reasonText: this.applyForm.reasonText,
          evidence: evidence.length > 0 ? JSON.stringify(evidence) : null
        });
        this.$message.success('售后申请已提交');
        this.showApplyDialog = false;
        this.applyForm = { reasonCode: '', reasonText: '', evidenceFiles: [] };
        await this.loadAfterSales();
        await this.loadOrderDetail();
      } catch (e) {
        this.$message.error('申请失败: ' + (e.response?.data?.message || e.message));
      }
    },
    handleUploadSuccess(response, file) {
      if (response && response.url) {
        this.applyForm.evidenceFiles.push({ name: file.name, url: response.url });
      }
    },
    handleUploadRemove(file) {
      this.applyForm.evidenceFiles = this.applyForm.evidenceFiles.filter(f => f.url !== file.url);
    },
    async submitApply() {
      if (!this.applyForm.reasonCode) {
        this.$message.warning('请选择问题原因');
        return;
      }
      try {
        const evidence = this.applyForm.evidenceFiles.map(f => f.url);
        await api.post('/after-sales/apply', {
          orderId: this.order.id,
          customerId: this.order.customerId,
          reasonCode: this.applyForm.reasonCode,
          reasonText: this.applyForm.reasonText,
          evidence: evidence.length > 0 ? JSON.stringify(evidence) : null
        });
        this.$message.success('售后申请已提交');
        this.showApplyDialog = false;
        this.applyForm = { reasonCode: '', reasonText: '', evidenceFiles: [] };
        await this.loadAfterSales();
        await this.loadOrderDetail();
      } catch (e) {
        this.$message.error('申请失败: ' + (e.response?.data?.message || e.message));
      }
    },
    asStatusClass(status) {
      const m = { PENDING_REVIEW: 'status-pending', REVIEWED: 'status-reviewed', COMPLETED: 'status-completed', REJECTED: 'status-rejected', CANCELLED: 'status-cancelled' };
      return m[status] || '';
    },
    asStatusText(status) {
      const m = { 
        PENDING_REVIEW: this.$t('wholesaler.WholesalerOrderDetail.pending'), 
        REVIEWED: this.$t('wholesaler.WholesalerOrderDetail.dispatchedStatus'), 
        COMPLETED: this.$t('wholesaler.WholesalerOrderDetail.deliveredStatus'), 
        REJECTED: this.$t('wholesaler.WholesalerOrderDetail.cancelledStatus'), 
        CANCELLED: this.$t('wholesaler.WholesalerOrderDetail.cancelledStatus') 
      };
      return m[status] || status;
    },
    asReasonIcon(code) {
      const m = { DAMAGED: '💔', WRONG_ITEM: '❌', NOT_RECEIVED: '📦', WRONG_SPEC: '📐', MISSING: '🔍', NOT_WANTED: '🙅' };
      return m[code] || '📝';
    },
    asReasonText(code) {
      const m = { 
        DAMAGED: this.$t('afterSales.reasonDamaged'), 
        WRONG_ITEM: this.$t('afterSales.reasonWrongItem'), 
        NOT_RECEIVED: this.$t('afterSales.reasonNotReceived'), 
        WRONG_SPEC: this.$t('afterSales.reasonWrongSpec'), 
        MISSING: this.$t('afterSales.reasonMissingItem'), 
        NOT_WANTED: this.$t('afterSales.reasonDontWant') 
      };
      return m[code] || code;
    },
    asResolutionText(type) {
      const m = { 
        RETURN_REFUND: this.$t('afterSales.resolutionReturnRefund'), 
        EXCHANGE: this.$t('afterSales.resolutionExchange'), 
        RESEND: this.$t('afterSales.resolutionResend'), 
        PARTIAL_REFUND: this.$t('afterSales.resolutionPartialRefund'), 
        RELIEF: this.$t('afterSales.resolutionRelief'), 
        NO_ACTION: this.$t('afterSales.resolutionNoAction') 
      };
      return type ? (m[type] || type) : this.$t('wholesaler.WholesalerOrderDetail.pendingProcessing');
    },
    asResolutionStatusText(status) {
      const m = { 
        PENDING: this.$t('wholesaler.WholesalerOrderDetail.pendingExecution'), 
        IN_PROGRESS: this.$t('wholesaler.WholesalerOrderDetail.executing'), 
        COMPLETED: this.$t('wholesaler.WholesalerOrderDetail.deliveredStatus'), 
        FAILED: this.$t('wholesaler.WholesalerOrderDetail.executionFailed') 
      };
      return status ? (m[status] || status) : '';
    },
    centsToAmount(cents) {
      const n = Number(cents || 0);
      return n >= 100 ? (n / 100).toFixed(2) : n.toFixed(2);
    },
    async printRefundReceipt() {
      if (this.afterSales.length === 0) {
        this.$message.warning('暂无售后记录');
        return;
      }
      const id = this.afterSales[0].id;
      try {
        const res = await api.get(`/after-sales/${id}/receipt`);
        if (res && res.receipt) {
          // 通过易联云打印
          await api.post(`/wholesaler/printer/print-after-sales/${id}`);
          this.$message.success('退单小票已推送打印');
        } else {
          this.$message.warning('退单小票内容为空');
        }
      } catch (e) {
        this.$message.error('打印失败: ' + (e.response?.data?.message || e.message));
      }
    }
  }
};
</script>

<style scoped>
.wholesaler-order-detail { min-height: 100vh; background: #f0f2f5; width: 100%; max-width: 100vw; overflow-x: hidden; }

.page-header {
  padding: 16px; background: #fff; border-bottom: 1px solid #e2e8f0;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.page-header .back-btn {
  font-size: 13px; color: #64748b; cursor: pointer;
  min-width: 44px; min-height: 44px;
  display: flex; align-items: center; justify-content: center;
  padding: 8px; border-radius: 8px;
  transition: background 0.2s;
}
.page-header .back-btn:active { background: #f1f5f9; }
.page-header h1 { font-size: 18px; font-weight: 700; margin-top: 4px; }
.page-header .order-no {
  font-size: 12px; color: #94a3b8; margin-top: 4px;
  font-family: 'SF Mono', 'Menlo', monospace;
}

.detail-content { padding-bottom: 20px; }

/* 状态卡片 */
.status-bar {
  margin: 12px 12px 0; border-radius: 14px; padding: 20px 16px;
  display: flex; align-items: center; gap: 14px; color: #fff;
  position: relative; overflow: hidden;
}
.status-bar::after {
  content: ''; position: absolute; top: -50%; right: -20%;
  width: 200px; height: 200px; border-radius: 50%;
  background: rgba(255,255,255,0.06);
}
.status-bar.paid { background: linear-gradient(135deg, #10b981, #059669); }
.status-bar.unpaid { background: linear-gradient(135deg, #f59e0b, #d97706); }
.status-bar.processing { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.status-bar .s-icon { font-size: 38px; z-index: 1; }
.status-bar .s-info { z-index: 1; }
.status-bar .s-title { font-size: 18px; font-weight: 700; }
.status-bar .s-sub { font-size: 12px; opacity: 0.8; margin-top: 2px; }

/* 时间线 */
.timeline-wrap {
  margin: 12px 12px 0; background: #fff; border-radius: 14px;
  padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.timeline-wrap h3 { font-size: 14px; font-weight: 700; margin-bottom: 16px; }
.timeline { display: flex; position: relative; padding: 0 10px; }
.timeline::before {
  content: ''; position: absolute; top: 16px; left: 24px; right: 24px;
  height: 3px; background: #e2e8f0; border-radius: 2px; z-index: 0;
}
.timeline .track {
  position: absolute; top: 16px; left: 24px;
  height: 3px; background: linear-gradient(90deg, #10b981, #6366f1);
  border-radius: 2px; z-index: 0; transition: width 0.4s ease;
}
.tl-step {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  position: relative; z-index: 1;
}
.tl-dot {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; margin-bottom: 8px;
  background: #e2e8f0; color: #94a3b8;
}
.tl-dot.done { background: #10b981; color: #fff; }
.tl-dot.current { background: #6366f1; color: #fff; animation: pulse 2s infinite; }
.tl-label { font-size: 11px; color: #94a3b8; text-align: center; line-height: 1.2; }
.tl-label.active { color: #6366f1; font-weight: 600; }
.tl-time { font-size: 10px; color: #cbd5e1; margin-top: 3px; }

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.3); }
  50% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
}

/* 模块通用 */
.module {
  margin: 12px 12px 0; background: #fff; border-radius: 14px;
  overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.module-header {
  padding: 14px 16px; display: flex; align-items: center; gap: 8px;
  border-bottom: 1px solid #f1f5f9;
}
.module-header .icon { font-size: 20px; }
.module-header .title { font-size: 15px; font-weight: 700; color: #1e293b; flex: 1; }
.badge {
  font-size: 11px; padding: 2px 10px; border-radius: 20px; font-weight: 600;
}
.badge.blue { background: #dbeafe; color: #1e40af; }
.badge.green { background: #dcfce7; color: #166534; }
.badge.red { background: #fee2e2; color: #991b1b; }
.badge.orange { background: #fff7ed; color: #c2410c; }
.nav-arrow { color: #94a3b8; font-size: 14px; }
.module-body { padding: 12px 16px; }

.row {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 8px 0; font-size: 13px;
}
.row + .row { border-top: 1px solid #f8fafc; }
.row .label { color: #94a3b8; flex-shrink: 0; min-width: 70px; }
.row .value { color: #334155; font-weight: 500; text-align: right; word-break: break-all; }
.mono { font-family: 'SF Mono', 'Menlo', monospace; font-size: 12px; }
.mono.small { font-size: 10px; }

/* 商品 */
.prod-item {
  display: flex; gap: 10px; padding: 10px 0; border-bottom: 1px solid #f8fafc;
}
.prod-item:last-child { border-bottom: none; }
.prod-info { flex: 1; min-width: 0; }
.prod-name { font-size: 13px; font-weight: 600; color: #1e293b; }
.prod-spec { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.prod-tax {
  font-size: 10px; padding: 1px 6px; border-radius: 4px; display: inline-block; margin-top: 2px;
}
.prod-tax.gst { background: #fef3c7; color: #92400e; }
.prod-tax.free { background: #dcfce7; color: #166534; }
.prod-right { text-align: right; flex-shrink: 0; }
.prod-amount { font-size: 14px; font-weight: 700; color: #1e293b; }
.prod-qty { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.amt-row {
  display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px;
}
.amt-row .label { color: #94a3b8; }
.amt-row .value { color: #334155; }
.amt-row.total {
  border-top: 2px solid #f1f5f9; margin-top: 8px; padding-top: 12px;
}
.amt-row.total .label { font-size: 15px; font-weight: 700; color: #1e293b; }
.amt-row.total .value { font-size: 20px; font-weight: 800; color: #6366f1; }

/* 客户 */
.ws-avatar {
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.ws-name { font-size: 14px; font-weight: 700; color: #1e293b; }
.ws-desc { font-size: 11px; color: #94a3b8; margin-top: 2px; }

/* 支付 */
.pay-status { text-align: center; padding: 8px 0 4px; }
.pay-badge {
  display: inline-block; padding: 6px 24px; border-radius: 24px;
  font-size: 15px; font-weight: 700;
}
.pay-badge.green { background: #dcfce7; color: #166534; }
.pay-badge.red { background: #fee2e2; color: #991b1b; }
.pay-badge.yellow { background: #fef3c7; color: #92400e; }
.total-amt { font-size: 18px !important; font-weight: 800 !important; color: #6366f1 !important; }

/* 售后 */
.empty-state { text-align: center; padding: 24px 0; color: #94a3b8; }
.empty-state .empty-icon { font-size: 32px; margin-bottom: 8px; }
.empty-state .empty-text { font-size: 13px; }
.as-card {
  background: #f8fafc; border-radius: 8px; padding: 10px 12px;
  margin-bottom: 8px; cursor: pointer; border-left: 3px solid #e2e8f0;
  transition: border-color 0.2s;
}
.as-card:hover { border-left-color: #1890ff; }
.as-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.as-no { font-size: 12px; font-weight: 600; color: #4b5563; font-family: monospace; }
.as-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.as-status.status-pending { background: #fef3c7; color: #92400e; }
.as-status.status-reviewed { background: #dbeafe; color: #1e40af; }
.as-status.status-completed { background: #f6ffed; color: #52c41a; }
.as-status.status-rejected { background: #fff2f0; color: #ff4d4f; }
.as-status.status-cancelled { background: #fafafa; color: #999; }
.as-body { display: flex; justify-content: space-between; align-items: center; }
.as-left { flex: 1; }
.as-reason { font-size: 12px; margin-bottom: 2px; }
.as-time { font-size: 10px; color: #bbb; }
.as-right { text-align: right; margin-left: 8px; }
.as-amount { font-size: 14px; color: #ff4d4f; font-weight: 600; }
.as-sub { font-size: 10px; color: #1890ff; margin-top: 2px; }

/* 付款凭证 */
.proof-image {
  max-width: 100%; max-height: 200px; border-radius: 8px;
  cursor: pointer; border: 2px solid #e2e8f0;
  transition: transform 0.2s;
}
.proof-image:hover { transform: scale(1.02); border-color: #6366f1; }

/* 流水记录状态 */
.record-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.record-status.pending { background: #fef3c7; color: #92400e; }
.record-status.success { background: #dcfce7; color: #166534; }
.record-status.failed { background: #fee2e2; color: #991b1b; }

/* 申请售后弹窗 */
.apply-info {
  background: #f0f7ff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #457b9d;
  margin-bottom: 16px;
}
.form-section { margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.reason-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.reason-tag {
  padding: 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.reason-tag.active { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }
.form-actions { display: flex; gap: 8px; }

/* 打印机 */
.printer-line { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
.printer-line .dot { width: 8px; height: 8px; border-radius: 50%; }
.printer-line .dot.on { background: #10b981; }
.printer-line .dot.off { background: #ef4444; }
.printer-line .text { font-size: 13px; color: #334155; }

/* 流水记录 */
.record-list { display: flex; flex-direction: column; gap: 10px; }
.record-item {
  padding: 12px; background: #f8fafc; border-radius: 8px;
  border-left: 3px solid #6366f1;
}
.record-item.is-pending {
  background: #fffbeb;
  border-left-color: #f59e0b;
}
.record-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.record-no { font-size: 12px; font-weight: 600; color: #4b5563; font-family: monospace; }
.record-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.record-status.pending { background: #fef3c7; color: #92400e; }
.record-status.success { background: #dcfce7; color: #166534; }
.record-status.failed { background: #fee2e2; color: #991b1b; }
.record-body { display: flex; justify-content: space-between; margin-bottom: 4px; }
.record-amount { font-size: 15px; font-weight: 700; color: #1e293b; }
.record-method { font-size: 12px; color: #6b7280; }
.record-footer { display: flex; justify-content: space-between; font-size: 11px; color: #9ca3af; }
.record-confirmed { color: #10b981; }
.record-actions { display: flex; gap: 8px; margin-top: 10px; justify-content: flex-end; }
/* 付款凭证 */
.record-proof { margin-top: 10px; text-align: center; }
.proof-image {
  max-width: 100%; max-height: 200px; border-radius: 8px;
  cursor: pointer; border: 2px solid #e2e8f0;
  transition: transform 0.2s;
}
.proof-image:hover { transform: scale(1.02); border-color: #6366f1; }

/* 底部操作栏 */
.action-bar {
  position: fixed; bottom: 0; left: 50%; transform: translateX(-50%);
  width: 100%; max-width: 480px; background: #fff;
  padding: 10px 16px; display: flex; gap: 8px;
  border-top: 1px solid #e2e8f0; z-index: 100;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
}

/* 安排配送按钮 */
.btn-arrange-big {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 16px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102,126,234,0.3);
  transition: all 0.2s;
}
.btn-arrange-big:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102,126,234,0.4);
}
.btn-arrange-big:active {
  transform: translateY(0);
}

/* 配送任务弹窗样式 */
.task-type-selection { margin-bottom: 16px; }
.task-radio-group {
  width: 100%;
  display: flex;
  gap: 10px !important;
}
.task-radio-group .el-radio-button { flex: 1; }
.task-radio-group .el-radio-button__inner { width: 100%; text-align: center; }

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.warehouse-selection, .driver-selection {
  margin-bottom: 16px;
}

.warehouse-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.warehouse-card {
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.warehouse-card.selected {
  border-color: #667eea;
  background: rgba(102,126,234,0.08);
}

.warehouse-card:hover { border-color: #667eea; }

.warehouse-card.insufficient {
  border-color: #f0d0d0;
  background: #fff8f8;
  cursor: not-allowed;
  opacity: 0.6;
}

.warehouse-info { flex: 1; }

.warehouse-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
}

.product-stocks {
  margin-top: 8px;
}

.no-stock {
  font-size: 11px;
  color: #f59e0b;
}

.stock-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
}

.stock-name {
  color: #333;
  min-width: 60px;
}

.stock-qty {
  color: #666;
}

.driver-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.driver-card {
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.driver-card.selected {
  border-color: #667eea;
  background: rgba(102,126,234,0.08);
}

.driver-card:hover { border-color: #667eea; }

.driver-info { flex: 1; }
.driver-name { font-size: 14px; font-weight: 600; color: #2c3e50; }
.driver-phone { font-size: 12px; color: #999; margin-top: 2px; }
.form-tip { font-size: 11px; color: #999; margin-top: 4px; }

/* 分享按钮样式 */
.btn-share-small {
  padding: 4px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 8px;
}
.btn-share-small:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.btn-share-small:active {
  transform: translateY(0);
}

/* 支付方式标签 */
.payment-method-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.payment-tag {
  padding: 8px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  background: #fff;
}
.payment-tag:hover {
  border-color: #667eea;
  background: #f0f4ff;
}
.payment-tag.active {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102,126,234,0.3);
}

/* 收款弹窗 */
.payment-info {
  background: #f0f7ff;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  color: #457b9d;
  margin-bottom: 16px;
  font-weight: 600;
}
</style>
